import { useState, useEffect, useMemo } from 'react';
import AdvanceTable from '../../../Utils/AdvanceTables.js';
import { getData } from '../../../API/GlobalApi.js';
import { OtherLedgerDetails } from '../../../Component/Modals/OtherLedgerDetails.js';

const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const d = new Date(isoString);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
};

const DEBOUNCE_MS = 400;

export const OtherLedgerPaymentdetaisl = ({ searchQuery = '', onCountChange }) => {
    const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageCounts, setPageCounts] = useState(0);
    const [loading, setLoading] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isLedgerOpen, setIsLedgerOpen] = useState(false);

    const handleRowClick = (_orgId, row) => {
        const original = row?.original;
        if (!original) return;
        setSelectedRow(original);
        setIsLedgerOpen(true);
    };

    const handleCloseLedger = () => {
        setIsLedgerOpen(false);
        setSelectedRow(null);
    };

    // Debounce search + reset to page 0 on new query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setCurrentPage(0);
        }, DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Single effect: list + search + pagination
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const params = new URLSearchParams();
            params.set('page', currentPage + 1); // API is 1-indexed
            if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());

            const result = await getData(`finance/payments/non-student/?${params.toString()}`);
            if (result) {
                // Map transaction_id → id so AdvanceTable highlights only the clicked row
                const rows = (result.results ?? []).map((item) => ({
                    ...item,
                    id: item.transaction_id,
                }));
                setData(rows);
                setPageCounts(result.total_pages ?? 1);
                onCountChange?.(result.count ?? rows.length);
            }
            setLoading(false);
        };

        fetchData();
    }, [debouncedSearch, currentPage]);

    const handlePageChange = ({ selected }) => {
        setCurrentPage(selected);
    };

    const columns = useMemo(
        () => [
            {
                Header: 'Name',
                accessor: 'user_name',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div>
                        <p
                            className="name-clickable"
                            style={{
                                textAlign: 'left',
                                color: '#222F3E',
                                fontWeight: 500,
                                margin: 0,
                                cursor: 'pointer',
                                display: 'inline-block',
                            }}
                            role="button"
                            tabIndex={0}
                            title="View Ledger Details"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRow(row.original);
                                setIsLedgerOpen(true);
                            }}
                        >
                            {row.original.user_name || '-'}
                        </p>
                        <p style={{ textAlign: 'left', color: '#888', fontSize: '12px', margin: 0 }}>
                            {row.original.receipt_number || ''}
                        </p>
                    </div>
                ),
            },
            {
                Header: 'Contact number',
                accessor: 'user_mobile',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div>
                        <p style={{ textAlign: 'left', color: '#222F3E', margin: 0 }}>
                            {row.original.user_mobile || '-'}
                        </p>
                    </div>
                ),
            },
            {
                Header: 'Amount',
                accessor: 'amount',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div>
                        <p style={{ textAlign: 'left', color: '#222F3E', margin: 0 }}>
                            ₹{row.original.amount || '0'}
                        </p>
                    </div>
                ),
            },
            {
                Header: 'Payment Method',
                accessor: 'payment_method',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div>
                        <p style={{ textAlign: 'left', color: '#222F3E', margin: 0 }}>
                            {row.original.payment_method || '-'}
                        </p>
                    </div>
                ),
            },
            {
                Header: 'Status',
                accessor: 'status',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div>
                        <span style={{
                            padding: '2px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 500,
                            background: row.original.status === 'Received' ? '#E6F9F1' : '#FDE2E2',
                            color: row.original.status === 'Received' ? '#27AE60' : '#E74C3C',
                        }}>
                            {row.original.status || '-'}
                        </span>
                    </div>
                ),
            },
            {
                Header: 'Payment Date',
                accessor: 'created_at',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div>
                        <p style={{ textAlign: 'left', color: '#222F3E', margin: 0 }}>
                            {formatDateTime(row.original.created_at)}
                        </p>
                    </div>
                ),
            },
            {
                Header: 'Created By',
                accessor: 'created_by_name',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div>
                        <p style={{ textAlign: 'left', color: '#222F3E', margin: 0 }}>
                            {row.original.created_by_name || '-'}
                        </p>
                    </div>
                ),
            },
        ],
        []
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '0 12px' }}>
                {loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: '#888' }}>
                        Loading...
                    </div>
                ) : data.length > 0 ? (
                    <AdvanceTable
                        columns={columns}
                        data={data}
                        pageCounts={pageCounts}
                        handlePageChange={handlePageChange}
                        currentPage={currentPage}
                        handleRowClick={handleRowClick}
                    />
                ) : (
                    <div style={{
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        flex: 1, color: '#888', textAlign: 'center',
                    }}>
                        No records found.
                    </div>
                )}
            </div>

            <OtherLedgerDetails
                isOpen={isLedgerOpen}
                onClose={handleCloseLedger}
                selectedRow={selectedRow}
            />
        </div>
    );
};
